import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfigContext";

const SCRIPT = [
  { from: "user", text: "My tire blew out and I'm stuck on the side of the road." },
  { from: "ai", text: "Got it. I'll help you find nearby roadside assistance for a tire replacement or tow." },
];

export default function AIDemo() {
  const { config } = useSiteConfig();
  const t = config.t.aiDemo;
  const [step, setStep] = useState(-1);
  const [typing, setTyping] = useState(false);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver((e) => {
      if (e[0].isIntersecting && !started.current) {
        started.current = true;
        run();
      }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const run = async () => {
    for (let i = 0; i < SCRIPT.length; i++) {
      if (SCRIPT[i].from === "ai") { setTyping(true); await wait(1100); setTyping(false); }
      else { await wait(500); }
      setStep(i);
      await wait(900);
    }
  };
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#0A0A0A] py-28 text-white sm:py-40" data-testid="ai-demo-section">
      <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-ai/10 blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-brand/20 blur-[120px]" />
      <div className="mx-auto max-w-3xl px-6 text-center">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="text-sm font-semibold uppercase tracking-[0.2em] text-ai">{t.eyebrow}</motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="mx-auto mt-4 max-w-2xl text-4xl font-bold tracking-tighter sm:text-5xl lg:text-6xl">
          {t.heading}
        </motion.h2>

        <div className="mx-auto mt-14 max-w-md space-y-4 text-left">
          {SCRIPT.map((m, i) => (
            <AnimatePresence key={i}>
              {step >= i && (
                <motion.div
                  initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className={m.from === "user" ? "flex justify-end" : "flex items-start gap-3"}>
                  {m.from === "ai" && (
                    <div className="mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ai/15 text-ai ring-1 ring-ai/30">
                      <Sparkles className="h-4 w-4" />
                    </div>
                  )}
                  <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 text-[15px] leading-relaxed ${m.from === "user"
                    ? "rounded-br-md bg-brand font-medium text-white"
                    : "rounded-bl-md bg-white/5 text-white/90 ring-1 ring-white/10"}`}>
                    {m.text}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          ))}

          {typing && (
            <div className="flex items-start gap-3">
              <div className="mt-1 grid h-9 w-9 place-items-center rounded-full bg-ai/15 text-ai ring-1 ring-ai/30"><Sparkles className="h-4 w-4" /></div>
              <div className="flex gap-1.5 rounded-2xl rounded-bl-md bg-white/5 px-5 py-4 ring-1 ring-white/10">
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} className="h-2 w-2 rounded-full bg-white/60"
                    animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
